import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { track } from "@/lib/analytics";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Persist the resolved user object to localStorage so an in-flight tab,
// a remount, or a brief network blip on Back-navigation NEVER renders the
// "Please sign in" screen to an already-authed user. The cached copy is
// trusted for first paint; /auth/me revalidates in the background.
const USER_CACHE_KEY = "rf_user_cache_v1";
function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeCachedUser(user) {
  try {
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch { /* quota / private mode */ }
}

export function AuthProvider({ children }) {
  // Hydrate from cache immediately so back-navigation never flashes a sign-in
  // gate while /auth/me is in flight.
  const [user, setUserState] = useState(() => readCachedUser());
  const [loading, setLoading] = useState(true);
  const lastCheckRef = useRef(0);

  const setUser = useCallback((next) => {
    setUserState((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      writeCachedUser(resolved);
      return resolved;
    });
  }, []);

  const checkAuth = useCallback(async () => {
    // Skip /me if we're on an OAuth callback URL — the callback page handles it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    lastCheckRef.current = Date.now();
    try {
      // ALWAYS call /auth/me. We used to short-circuit when no JWT and no
      // readable cookie was present, but the Emergent Google OAuth session
      // cookie is httpOnly — invisible to document.cookie — so OAuth users
      // were being treated as anonymous on every reload. /auth/me returns
      // {user: null} for anon, so calling it unconditionally is safe.
      const { data } = await api.get("/auth/me");
      if (data && data.user === null) {
        setUser(null);
      } else {
        setUser(data);
      }
    } catch (err) {
      // Only clear the session on explicit 401/403 (real auth failure).
      // Transient errors (5xx, network, deploy blip) must NOT log the user
      // out — otherwise a back-navigation during a Wi-Fi flicker silently
      // signs them out.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem("rf_token");
        setUser(null);
      }
      // else: keep prior state (cached user) intact.
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Revalidate session when the tab returns to the foreground. This catches
  // stale state if the user left the tab open for a long time (e.g. 90+ days
  // and the JWT expired) without forcing a logout on every page navigation.
  useEffect(() => {
    const onFocus = () => {
      // Throttle: don't re-hit /auth/me more than once every 30s.
      if (Date.now() - lastCheckRef.current < 30_000) return;
      checkAuth();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("rf_token", data.token);
    setUser(data.user);
    track("login", { method: "email" });
    return data.user;
  };

  const register = async (email, password, name, referralCode = null) => {
    const body = { email, password, name };
    if (referralCode) body.referral_code = referralCode;
    const { data } = await api.post("/auth/register", body);
    localStorage.setItem("rf_token", data.token);
    setUser(data.user);
    track("sign_up", { method: "email", referred: !!referralCode });
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("rf_token");
    Object.keys(localStorage).filter((k) => k.startsWith("rf_shop_")).forEach((k) => localStorage.removeItem(k));
    setUser(null);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
